import { mdiConnection, mdiFileGifBox, mdiImage, mdiLink, mdiVideo } from '@mdi/js'
import CardList from 'components/CardList'
import { FetchableProps } from 'components/ContentGridView'
import EmptyView from 'components/EmptyView'
import { NO_CONTENT_MESSAGE_CAROUSEL, PROFILES_NOT_CONNECTED_MESSAGE } from 'components/ErrorOverlay'
import { FetchFeedPageContent, FetchStreamPageContent } from 'components/Fetch'
import { TIMERANGE_FILTER_DEFAULT } from 'config'
import {
  ALL_FILTER,
  ALL_RANGE_FILTER,
  AnyContent,
  ARTICLE_TYPE,
  ContentItem,
  ContentSource,
  Feed,
  GIF_TYPE,
  PHOTO_TYPE,
  RangeFilter,
  rangeFiltersAscending,
  Stream,
  VIDEO_TYPE
} from 'interfaces'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { instanceOfStream } from 'services/content/util'
import { CarouselContentState, getInitialContentState, getUpdatedContentState, mergeContentState } from './carouselContent'
import styles from './ContentLayout.pcss'
import { useCarouselCards } from './useCarouselCards'
import { FilterType, PostDestination } from 'shared'

export interface CarouselViewOwnProps {
  filter: FilterType
  source?: ContentSource
  items?: AnyContent[]
  loading?: boolean
  defaultRange?: RangeFilter
  destinations?: { [key: string]: Readonly<PostDestination> }
  sortByDate?: boolean
  skipContentFeatureChecks?: boolean
  className?: string

  onContentItemClick?(content: ContentItem): void
  onContentItemCompose?(content: ContentItem): void | boolean
}

interface CarouselViewState {
  loading: boolean
  range: RangeFilter
  content: CarouselContentState
  error?: any
}

interface RenderErrorProps {
  filter: FilterType
  loading: boolean
  myProfilesNotConnected: boolean
  intl: IntlShape
}

const renderErrorIcon = (filter: FilterType): string => {
  switch (filter) {
    case ARTICLE_TYPE:
      return mdiLink
    case PHOTO_TYPE:
      return mdiImage
    case GIF_TYPE:
      return mdiFileGifBox
    case VIDEO_TYPE:
      return mdiVideo
    default:
      return ''
  }
}

const RenderError = ({ filter, loading, myProfilesNotConnected, intl }: RenderErrorProps) => {
  if (loading) {
    return null
  }

  if (myProfilesNotConnected) {
    return (
      <EmptyView
        title={PROFILES_NOT_CONNECTED_MESSAGE(intl.formatMessage)}
        icon={mdiConnection}
        carousel
        subtitle={(
          <p key="error-link">
            <a href="/settings/networks">Click here</a> to connect your accounts.
          </p>
        )}
      />
    )
  }

  return (
    <EmptyView
      title={NO_CONTENT_MESSAGE_CAROUSEL(filter, intl.formatMessage)}
      icon={renderErrorIcon(filter)}
      carousel
    />
  )
}

export function CarouselView(props: CarouselViewOwnProps) {
  const intl = useIntl()
  const initialItems = props.items || []

  const [state, setState] = useState<CarouselViewState>({
    loading: props.loading !== undefined ? props.loading : true,
    range: props.defaultRange || TIMERANGE_FILTER_DEFAULT,
    content: getInitialContentState(initialItems)
  })

  const myProfilesNotConnected = useMemo(() => {
    if (!props.source) {
      return false
    }
    return Object.keys(props.destinations || {}).length === 0 && (props.source as any).protected
  }, [props.source, props.destinations])

  const onFetchFailed = useCallback((error: any) => {
    setState(prevState => {
      const currentTypeFilter = props.filter
      const currentRangeFilter = prevState.range
      const filteredItems = prevState.content[currentTypeFilter].items

      if (filteredItems.length === 0 && error.type === currentTypeFilter) {
        const nextRange = rangeFiltersAscending.indexOf(currentRangeFilter) + 1

        if (nextRange !== 0 && nextRange < rangeFiltersAscending.length && currentRangeFilter !== ALL_RANGE_FILTER) {
          return {
            ...prevState,
            content: {
              ...prevState.content,
              [currentTypeFilter]: { page: 0, items: [], hasMore: true }
            },
            range: rangeFiltersAscending[nextRange] as RangeFilter
          }
        }

        if (currentRangeFilter === ALL_RANGE_FILTER) {
          return {
            ...prevState,
            content: {
              ...prevState.content,
              [currentTypeFilter]: { ...prevState.content[currentTypeFilter], hasMore: false }
            },
            loading: false,
            error
          }
        }
      }

      return { ...prevState, error }
    })
  }, [props.filter])

  const onContentFetched = useCallback((content: AnyContent[]) => {
    setState(prevState => {
      // An empty page fires BOTH onFailed (which advances the range in onFetchFailed)
      // and this onFetched([]) on the next animation frame. Ending the load here would
      // clobber that advance and abort the range cascade. While a wider range is still
      // available for a range-aware filter, defer to onFetchFailed instead of stopping.
      const rangeAwareFilter = props.filter !== ALL_FILTER && !String(props.filter).startsWith('stock')
      const moreRangesAvailable = prevState.range !== ALL_RANGE_FILTER
        && rangeFiltersAscending.indexOf(prevState.range) < rangeFiltersAscending.length - 1
      if (content.length === 0 && rangeAwareFilter && moreRangesAvailable) {
        return prevState
      }

      const newContent = getUpdatedContentState(content, prevState.content)
      const nextContent = mergeContentState(prevState.content, newContent)

      return { ...prevState, content: nextContent, loading: false }
    })
  }, [props.filter])

  const fetchNextPage = useCallback(() => {
    setState(previousState => {
      if (previousState.loading || (previousState.error && previousState.error.type === props.filter)) {
        return previousState
      }

      const currentTypeFilter = props.filter
      const page = previousState.content[currentTypeFilter].page + 1
      return {
        ...previousState,
        loading: true,
        content: {
          ...previousState.content,
          [currentTypeFilter]: {
            items: previousState.content[currentTypeFilter].items,
            page,
            hasMore: previousState.content[currentTypeFilter].hasMore
          }
        }
      }
    })
  }, [props.filter])

  const fetchElement = useMemo(() => {
    if (!state.loading) {
      return null
    }

    const currentTypeFilter = props.filter
    const currentContent = state.content[currentTypeFilter]

    if (props.items && props.items.length && currentContent.page === 0) {
      return null
    }

    if (!currentContent.hasMore || (currentContent.items.length !== 0 && currentContent.page === 0)) {
      return null
    }

    if (!props.source) {
      return null
    }

    const fetchProps: FetchableProps = {
      page: currentContent.page,
      range: state.range,
      onFailed: onFetchFailed,
      onFetched: onContentFetched
    }

    return instanceOfStream(props.source)
      ? (
        <FetchStreamPageContent
          key="stream-fetch"
          {...fetchProps as any}
          stream={props.source as Stream}
          only={currentTypeFilter}
        />
      )
      : (
        <FetchFeedPageContent
          key="feed-fetch"
          feed={props.source as Feed}
          {...fetchProps}
          only={currentTypeFilter}
        />
      )
  }, [
    state.loading,
    props.filter,
    state.content,
    props.items,
    props.source,
    state.range,
    onFetchFailed,
    onContentFetched
  ])

  // Handle source change
  useEffect(() => {
    if (props.source) {
      setState(prevState => {
        if (prevState.error?.source === props.source) {
          return prevState
        }
        return {
          ...prevState,
          loading: true,
          content: getInitialContentState([]),
          error: { ...prevState.error, source: props.source }
        }
      })
    }
  }, [props.source])

  // Handle filter change
  useEffect(() => {
    setState(prevState => {
      const currentContent = prevState.content[props.filter]
      if (currentContent.hasMore && currentContent.items.length === 0) {
        return { ...prevState, loading: true }
      }
      return prevState
    })
  }, [props.filter])

  // Handle items change
  useEffect(() => {
    setState(prevState => {
      const hasItems = !!props.items
      const prevItemsLength = prevState.content[ALL_FILTER].items.length
      if ((hasItems && prevItemsLength === 0) || (props.items || []).length !== prevItemsLength) {
        return {
          ...prevState,
          content: getUpdatedContentState(props.items || [], prevState.content),
          loading: false
        }
      }
      return prevState
    })
  }, [props.items])

  // Handle loading prop change
  useEffect(() => {
    if (props.loading !== undefined) {
      setState(prevState => {
        if (props.loading !== prevState.loading) {
          return { ...prevState, loading: !!props.loading }
        }
        return prevState
      })
    }
  }, [props.loading])

  const currentTypeFilter = props.filter
  const currentContentState = state.content[currentTypeFilter]

  const items = currentContentState.items
  const shouldRenderError = (items.length === 0 && !state.loading) || myProfilesNotConnected
  const error = shouldRenderError
    ? <RenderError filter={currentTypeFilter} loading={state.loading} myProfilesNotConnected={myProfilesNotConnected} intl={intl} />
    : null

  const cards = useCarouselCards({
    items,
    filter: currentTypeFilter,
    loading: state.loading,
    externalLoading: props.loading,
    sortByDate: props.sortByDate,
    skipFeatureChecks: props.skipContentFeatureChecks,
    onContentItemClick: props.onContentItemClick,
    onContentItemCompose: props.onContentItemCompose
  })

  return (
    <div className={`${styles['list-wrapper']} ${props.className || ''}`} data-testid="carousel-view">
      {fetchElement}
      <CardList
        key="list-view"
        onScrollLimit={fetchNextPage}
      >
        {cards}
      </CardList>
      {error}
    </div>
  )
}

export default CarouselView
